"use strict";
// One definition per rendered format for the backend's `YYYY-MM-DD` date
// strings, for web (Vite) and native (Metro) alike.
//
// WHY IT IS SHARED. This module used to exist twice, once per platform
// (`frontend/src/lib/apiDates.ts` and `mobile/src/lib/apiDates.ts`), and the two
// copies were themselves the second attempt at one source. BEN-141 2f:
// `api/forecastPlan.ts` formatted the next paycheck as `EEE, MMM d, yyyy` while
// `api/paycheck.ts` passed the raw ISO string straight through, so the web
// dashboard's Upcoming Paycheck card printed "Sunday, 2026-08-09" for the date
// Forecast printed as "Sun, Aug 9, 2026" — the same paycheck, from the same
// backend, on two pages one click apart. #744 then counted about fifteen more
// hand-rolled `new Date(iso + "T00:00:00").toLocaleDateString(...)` sites in
// mobile alone. A second module importing from the first is how that starts
// again, so there is now one module and both clients import it.
//
// WHY THERE IS A STYLE TABLE INSTEAD OF A FORMATTER PER CALL SITE. Every call
// site that passes its own `Intl` options bag is free to invent a format, and
// several did: the same date read "Aug 9" in one card and "August 9" in the one
// beside it. Adding a format here is adding a row to `API_DATE_STYLES`, which is
// reviewable in one place and testable against its expected output.
//
// WHY NOT date-fns. The web copy used `date-fns`; the native copy could not,
// because mobile does not carry it. `@fomofi/shared` has no runtime
// dependencies, so these use `Intl` over an explicitly LOCAL-parsed date. The
// two web formats are byte-identical to what `date-fns` produced for them, and
// `sharedApiDates.test.ts` on each side asserts the exact strings.
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseApiDate = parseApiDate;
exports.formatApiDate = formatApiDate;
exports.shortApiDate = shortApiDate;
exports.longApiDate = longApiDate;
exports.shortApiDateYearAware = shortApiDateYearAware;
/**
 * Parse a backend ISO date as a LOCAL calendar date.
 *
 * `new Date("2026-08-09")` is parsed as UTC midnight and shifts to Aug 8 in any
 * negative-offset timezone — the class of bug that hid today's rows from "Recent
 * transactions" on web, and a confirmed mobile bug class since the 2026-07-06
 * parity audit. Splitting the parts has no timezone in it. Call sites used to
 * dodge this by appending `"T00:00:00"`; that string no longer appears in
 * either client, and a guard test on each side keeps it that way.
 *
 * A value that is not `YYYY-MM-DD` (an ISO datetime, say) falls back to the
 * platform parser, so a caller that hands over a timestamp still gets a date
 * rather than `Invalid Date`.
 */
function parseApiDate(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match)
        return new Date(iso);
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}
const API_DATE_STYLES = {
    short: { month: "short", day: "numeric" },
    shortWithYear: { month: "short", day: "numeric", year: "numeric" },
    long: { weekday: "short", month: "short", day: "numeric", year: "numeric" },
    full: { weekday: "long", month: "long", day: "numeric", year: "numeric" },
    monthDay: { month: "long", day: "numeric" },
    weekdayMonthDay: { weekday: "long", month: "long", day: "numeric" },
    weekdayShortDay: { weekday: "long", month: "short", day: "numeric" },
    monthYear: { month: "long", year: "numeric" },
    monthYearShort: { month: "short", year: "numeric" },
};
/**
 * Render a backend ISO date, or an already-parsed `Date`, in one of the
 * product's formats. The locale is fixed at `en-US`: these strings are product
 * copy today, and a device locale silently reordering them would be a change
 * nobody asked for. Localisation is a decision, not a default.
 */
function formatApiDate(value, style, locale = "en-US") {
    const date = typeof value === "string" ? parseApiDate(value) : value;
    return date.toLocaleDateString(locale, API_DATE_STYLES[style]);
}
/** "Aug 9" — compact, for chart axes, ranges and inline captions. */
function shortApiDate(iso) {
    return formatApiDate(iso, "short");
}
/** "Sun, Aug 9, 2026" — the full form, weekday included. */
function longApiDate(iso) {
    return formatApiDate(iso, "long");
}
/**
 * "Sep 30", or "Sep 30, 2025" when the date is not in `today`'s year (R5,
 * 2026-10-05: "No transactions since {date}"). A stale account's last transaction
 * can be months back; across a year boundary "Sep 30" alone would read as the
 * wrong September. One helper so both clients print the same date.
 */
function shortApiDateYearAware(iso, today = new Date()) {
    const date = parseApiDate(iso);
    return formatApiDate(date, date.getFullYear() === today.getFullYear() ? "short" : "shortWithYear");
}
//# sourceMappingURL=apiDates.js.map